import { ConnectedUser } from "$src/domain/realtime/ConnectedUser";
import { IHubService } from "$src/domain/realtime/IHubService";
import { UserAction } from "$src/domain/realtime/UserAction";
import { User } from "$src/domain/users/UserSummary";
import { get } from "svelte/store";
import {
  addConnectedUser,
  removeConnectedUser,
  setConnectedUsers,
  updateConnectedUser,
} from "./connectedUserStore";
import {
  addTakeOverRequest,
  autoTakeOverTimerValue,
  clearTakeOverRequests,
  isTakeOverQueryInProgress,
  TakeOverRequest,
} from "./draftStore";
import { SignalRService } from "../../infrastructure/realtime/SignalRService";
import { toast } from "svelte-sonner";
import { t } from "svelte-i18n";
import { container } from "tsyringe";
import { RequestDraftToken } from "../projects/sub/drafts/commands/RequestDraft";
import { TakeOverDraft, TakeOverDraftToken } from "../projects/sub/drafts/commands/TakeOverDraft";
import { onTakeOverProjectDraft } from "../projects/stores/projectStore";
import { appQueryClient } from "../main/queryClient";

export class HubService extends SignalRService implements IHubService {
  public activeLocation: string | null = null;
  public activeUser: User | null = null;

  private takingOverIntervalId: NodeJS.Timeout | undefined;

  protected Initialize(): void {
    this.connection.onreconnecting((error) => {
      if (this.activeLocation && this.activeUser) {
        this.joinPage(this.activeLocation, this.activeUser);
      }
    });

    this.on("UserJoined", (user: ConnectedUser) => {
      console.log("user joined");
      addConnectedUser(user);
    });

    this.on("UserLeft", (user: ConnectedUser) => {
      console.log("user left");
      removeConnectedUser(user.connectionId);
    });

    this.on("UserStartAction", (user: ConnectedUser, action: UserAction, groupStoreId: number, projectId: number) => {
      updateConnectedUser(user);
      let message = "";
      switch (action) {
        case UserAction.EditingProject:
          message = get(t)('page.projects.draft.startEditing', { values: { user: `${user.firstname} ${user.lastname}`}});
          clearTakeOverRequests();
          break;
        case UserAction.Idle:
          message = get(t)('page.projects.draft.endEditing', { values: { user: `${user.firstname} ${user.lastname}`}});
          appQueryClient.invalidateQueries(['project', groupStoreId, projectId], { refetchActive: true });
          clearTakeOverRequests();
          break;
      }

      toast(message);
    });

    this.on("UserTakeOverDraft", (user: ConnectedUser) => {
      clearTakeOverRequests();

      onTakeOverProjectDraft();

      let message = get(t)('page.projects.draft.takeOverEditing', { values: { user: `${user.firstname} ${user.lastname}`}});
      toast(message);
    });

    this.on(
      "UserQueryTakingOver",
      (
        user: ConnectedUser,
        groupStoreId: number,
        projectId: number,
        autoAcceptDelayInSecond: number
      ) => {
        addTakeOverRequest(user, groupStoreId, projectId, autoAcceptDelayInSecond);
      }
    );

    this.on(
      "UserCancelTakingOver",
      (user: ConnectedUser, groupStoreId: number, projectId: number) => {
        clearTakeOverRequests();

        let message = get(t)('page.projects.draft.cancelTakeOver', { values: { user: `${user.firstname} ${user.lastname}`}});
        toast(message);
      }
    );

    this.on("ServerAllowTakingOver", async (groupStoreId: number, projectId: number) => {
        isTakeOverQueryInProgress.set(false);
      this.stopTakingOverTimer();

      clearTakeOverRequests();
      
      let message = get(t)('page.projects.draft.automaticTakeOver');
      toast(message);

      const query = container.resolve<TakeOverDraft>(TakeOverDraftToken);
      await query.execute(groupStoreId, projectId);
    });

    this.on("UserAllowTakingOver", (user: ConnectedUser, groupStoreId: number, projectId: number) =>  {
        isTakeOverQueryInProgress.set(false);
        this.stopTakingOverTimer();

      clearTakeOverRequests();
      
      let message = get(t)('page.projects.draft.takeOverAccepted', { values: { user: `${user.firstname} ${user.lastname}`}});
      toast(message);

      const takeOverCommand = container.resolve<TakeOverDraft>(TakeOverDraftToken);
      takeOverCommand.execute(groupStoreId, projectId).then(data => console.log('success', data))
      .catch(error => {
        console.log(error);
      });
    });

    this.on("UserDenyTakingOver", (user: ConnectedUser, groupStoreId: number, projectId: number) => {
        isTakeOverQueryInProgress.set(false);
      this.stopTakingOverTimer();

      clearTakeOverRequests();

      let message = get(t)('page.projects.draft.takeOverDenied', { values: { user: `${user.firstname} ${user.lastname}`}});
      toast(message);
    });

    this.on("ReceivePageUsers", (users: ConnectedUser[]) => {
      setConnectedUsers(users);
    });
  }

  async joinPage(pageUrl: string, user: User): Promise<void> {
    try {
      return await this.connection.send("JoinPage", pageUrl, user);
    } catch (err) {
      console.error("Join page error:", err);
    }
  }

  async queryTakingOverProject(groupStoreId: number, projectId: number): Promise<void> {
    try {
      this.takingOverIntervalId = setInterval(() => {
        if (get(autoTakeOverTimerValue) > 0) {
          autoTakeOverTimerValue.update((n) => n - 1);
        } else {
          this.stopTakingOverTimer();
        }
      }, 1000);

      isTakeOverQueryInProgress.set(true);

      return await this.connection.send("QueryTakingOver", groupStoreId, projectId);
    } catch (err) {
      console.error("query taking over project error:", err);
    }
  }

  private stopTakingOverTimer() {
    if (this.takingOverIntervalId) {
      clearInterval(this.takingOverIntervalId);
      this.takingOverIntervalId = undefined;
    }
  }

  async cancelTakingOverProject(groupStoreId: number, projectId: number): Promise<void> {
    try {
  
      if (this.takingOverIntervalId) {
        this.stopTakingOverTimer();

        await this.connection.send("CancelTakingOver", groupStoreId, projectId);
      }

      isTakeOverQueryInProgress.set(false);

    } catch (err) {
      console.error("cancel taking over project error:", err);
    }
  }

  async allowTakingOverProject(
    takeOverRequest: TakeOverRequest
  ): Promise<void> {
    try {
      takeOverRequest.stopTimer();

      clearTakeOverRequests();

      return await this.connection.send(
        "AllowTakingOver",
        takeOverRequest.groupStoreId,
        takeOverRequest.projectId,
        takeOverRequest.user.connectionId
      );
    } catch (err) {
      console.error("allow taking over project error:", err);
    }
  }

  async denyTakingOverProject(takeOverRequest: TakeOverRequest): Promise<void> {
    try {
      takeOverRequest.stopTimer();

      clearTakeOverRequests();

      return await this.connection.send(
        "DenyTakingOver",
        takeOverRequest.groupStoreId,
        takeOverRequest.projectId,
        takeOverRequest.user.connectionId
      );
    } catch (err) {
      console.error("deny taking over project error:", err);
    }
  }

  async startEditingProject(groupStoreId: number, projectId: number): Promise<void> {
    try {
      return await this.connection.send("StartEditingProject", groupStoreId, projectId);
    } catch (err) {
      console.error("Start editing project error:", err);
    }
  }

  async stopEditingProject(groupStoreId: number, projectId: number): Promise<void> {
    try {
      return await this.connection.send("StopEditingProject", groupStoreId, projectId);
    } catch (err) {
      console.error("Stop editing project error:", err);
    }
  }
}
